import {
  CapitalAccountsBanner,
  DocumentCategories,
  Spinner,
} from '../../../components';
import { useDocumentTitle } from '../../../hooks';
import {
  type CapitalAccountsStoreState,
  type DocumentsStoreState,
  fetchCapitalAccountsPreview,
  fetchCategories,
  fetchReports,
  type Filters,
  type FilterStoreState,
  type PeriodFilters} from '../../../store/slices';
import style from './Documents.module.scss';
import { dataRoomApi } from 'api/dataRoomApi';
import classNames from 'classnames';
import { DataRoom } from 'components/DataRoom/DataRoom';
import { RecentDocuments } from 'components/DocumentCategories/RecentDocuments/RecentDocuments';
import { ReportsModule } from 'components/ReportsModule/ReportsModule';
import { useEffect,
  useState } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isTablet } from 'services/mobile';
import { type AppDispatch } from 'store';

export const Documents = () => {
  const {
    categoriesPending,
    displayType,
    newCategory,
    otherCategories,
    reports,
    reportsPending,
  } = useSelector<{ documents: DocumentsStoreState, }>(
    (state) => state.documents,
  ) as DocumentsStoreState;

  const {
    hasCapitalAccountsPending,
  } = useSelector<{
    capitalAccounts: CapitalAccountsStoreState,
  }>(
    (state) => state.capitalAccounts,
  ) as CapitalAccountsStoreState;

  const activeFilters = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.activeFilters) as Filters | null;

  const periodFilters = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.usualDocumentsFilter) as PeriodFilters | null;
  useDocumentTitle('Investor Portal');
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [
    dataRoomFolders,
    setDataRoomFolders,
  ] = useState<string[]>([]);

  const fetchDataRoomFunds = async () => {
    if (activeFilters) {
      const res = await dataRoomApi.getDataRoomFolders({
        firmId: activeFilters.organizationId,
        fundIds: activeFilters.fundIds,
      });
      setDataRoomFolders(res.dataRoomFolders);
    }
  };

  const {
    hasCapitalAccounts,
  } = useSelector<{ capitalAccounts: CapitalAccountsStoreState, }>(
    (state) => state.capitalAccounts,
  ) as CapitalAccountsStoreState;

  const isTheOnlyOneBanner = (() => {
    const visibleComponents = [
      Boolean(reports?.length || !otherCategories?.length),
      Boolean(hasCapitalAccounts || !otherCategories?.length),
      Boolean(dataRoomFolders?.length || !otherCategories?.length),
    ].filter(Boolean).length;

    return visibleComponents === 1;
  })();
  // dont forget to change to hasCapitalAccounts in inVisible ...
  const reportsComponents = [
    {
      component: <ReportsModule isTheOnlyOneBanner={isTheOnlyOneBanner} />,
      isVisible: reports?.length || !otherCategories?.length,
      priority: reports?.length ? 1 : 4,
    },
    {
      component: <CapitalAccountsBanner isTheOnlyOneBanner={isTheOnlyOneBanner} />,
      isVisible: hasCapitalAccounts || !otherCategories?.length,
      priority: hasCapitalAccounts ? 2 : 5,
    },
    {
      component: <DataRoom
        folderNames={dataRoomFolders}
        isTheOnlyOneBanner={isTheOnlyOneBanner}
      />,
      isVisible: dataRoomFolders?.length || !otherCategories?.length,
      priority: dataRoomFolders?.length ? 3 : 6,
    },
  ];

  const sortedComponents = reportsComponents.sort((a, b) => a.priority - b.priority)
    .filter((component) => component.isVisible)
    .map((component) => component.component);

  useEffect(() => {
    if (!activeFilters) {
      navigate('/firms');
      return;
    }

    dispatch(fetchReports({
      firmId: activeFilters.organizationId,
      fundIds: activeFilters.fundIds,
    }));

    dispatch(fetchCapitalAccountsPreview({
      accountIds: activeFilters.accountIds,
      firmId: activeFilters.organizationId,
    }));

    if (activeFilters) {
      fetchDataRoomFunds();
    }
  }, [
    activeFilters,
    dispatch,
    navigate,
  ]);

  useEffect(() => {
    if (!activeFilters) {
      navigate('/firms');
      return;
    }

    dispatch(fetchCategories({
      ...activeFilters,
      displayType: displayType?.id,
      firmId: activeFilters.organizationId,
      quarters: periodFilters?.selectedQuarters,
    }));
  }, [
    dispatch,
    activeFilters,
    navigate,
    displayType?.id,
  ]);

  const getPageComponents = () => {
    if (hasCapitalAccountsPending || reportsPending) {
      return <Spinner styles='!h-[80vh]' />;
    }

    if (isTheOnlyOneBanner && !isTablet()) {
      return (
        <>
          <div className='flex max-h-48 w-full max-w-[90rem] gap-[1rem]'>
            <div className='w-2/3'>
              <RecentDocuments />
            </div>
            <div className={`!w-1/3 ${style.digitalReportsContainer}`}>
              {sortedComponents}
            </div>
          </div>
          <DocumentCategories />
        </>
      );
    }

    if (
      !hasCapitalAccounts &&
      !reports?.length &&
      !dataRoomFolders.length &&
      otherCategories?.length
    ) {
      return (
        <>
          <RecentDocuments />
          <DocumentCategories />
        </>
      );
    }

    return (
      <>
        <div className={style.digitalReportsContainer}>
          {sortedComponents}
        </div>
        <RecentDocuments />
        <DocumentCategories />
      </>
    );
  };

  return (
    <div className={classNames(style.documentsContainer, { 'h-full': categoriesPending || hasCapitalAccountsPending
      || reportsPending || !newCategory && !otherCategories?.length })}
    >
      {getPageComponents()}
    </div>
  );
};
